import { useCallback, useEffect, useMemo, useState } from 'react';
import queryString from 'query-string';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { ASC, DESC, VALUATION_DATE_KEY } from 'common/constants/analyst-dashboards';
import { AttioSortDirection, AttioValuationFilter } from 'dashboard409a/common/constants/analyst-dashboard';
import { CURRENT_JOBS_SLUG, OPPORTUNITIES_SLUG, PAST_JOBS_SLUG } from 'dashboard409a/common/constants/urls';
import { OperationsModal } from 'dashboard409a/components/OperationsModal';
import { GenericWorkboard, useNoteHandler } from 'dashboard409a/components/ValuationsTable/common';
import { ValuationsTableBulkData } from 'dashboard409a/components/ValuationsTable/ValuationsTableBulkData';
import { useValuationsWithBulkData } from 'dashboard409a/hooks/useValuationsWithBulkData';
import { CreateNoteRequest, SimplifiedAttioValuation } from 'dashboard409a/services/attio.types';
import { ModelWithValuationData } from 'dashboard409a/services/models.types';
import { RootStateType } from 'dashboard409a/states/store';
import { getInitialSortFromStorage, useFindSlug } from 'dashboard409a/utilities';
import { findStandaloneCompanyForValuation } from 'hooks/useCompanyLookup';

const VALUATIONS_SORT_KEY = 'valuationsSort';

interface WorkboardDataTableProps {
  filters: AttioValuationFilter;
  orderBy?: string;
  order?: AttioSortDirection;
}

const WorkboardDataTable = ({
  filters,
  orderBy: initialOrderBy = VALUATION_DATE_KEY,
  order: initialOrder = DESC,
}: WorkboardDataTableProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentSlug = useFindSlug();
  
  // Determine sectionId based on the current route
  const sectionId = useMemo(() => {
    if (currentSlug === CURRENT_JOBS_SLUG) {
      return CURRENT_JOBS_SLUG;
    }
    if (currentSlug === PAST_JOBS_SLUG) {
      return PAST_JOBS_SLUG;
    }
    if (currentSlug === OPPORTUNITIES_SLUG) {
      return OPPORTUNITIES_SLUG;
    }
    return undefined;
  }, [currentSlug]);
  
  // Initialize state from localStorage if available
  const initialSort = getInitialSortFromStorage(VALUATIONS_SORT_KEY, initialOrderBy, initialOrder);
  const [orderBy, setOrderBy] = useState(initialSort.sortBy);
  const [order, setOrder] = useState<AttioSortDirection>(initialSort.sortOrder);
  const [openModal, setOpenModal] = useState(false);
  const [selectedValuation, setSelectedValuation] = useState<SimplifiedAttioValuation | undefined>();

  // Get companies from Redux to find company_slug
  const companies = useSelector((state: RootStateType) => state.user.standalone_companies_permissions);
  
  // Parse query params
  const queryParams = useMemo(() => queryString.parse(location.search), [location.search]);
  const attioIdFromUrl = queryParams['attio-id'] as string | undefined;

  const {
    rows,
    isLoading,
    updateValuationStatus,
    updateValuationNotes,
    updateValuationEngagementManager,
  } = useValuationsWithBulkData({
    filters,
    orderBy,
    order,
  });

  // Use the generic note handler
  const { isNoteLoading, noteError, handleNoteSave: handleNoteSaveGeneric } = useNoteHandler({
    onSuccess: (id, noteContent) => {
      // For both creates and updates, use the id parameter (which is the valuationId)
      updateValuationNotes(id as string, noteContent);
    },
  });


  // Convert SimplifiedAttioValuation to ModelWithValuationData format for the modal
  // Note: Only minimal properties are needed since OperationsModal only uses attio_valuation_id and company_slug
  const selectedModel = useMemo((): ModelWithValuationData | undefined => {
    if (!selectedValuation) {
      return undefined;
    }

    // Find the matching company using robust lookup function (handles name variations)
    const matchingCompany = selectedValuation && companies
      ? findStandaloneCompanyForValuation(selectedValuation, companies)
      : null;

    // Create minimal ModelWithValuationData object with only required properties
    // id is optional in Fulfillment type, so we omit it since it's not used for Jobs valuations
    return {
      attio_valuation_id: selectedValuation.record_id,
      company_slug: matchingCompany?.slug || '',
      company_name: selectedValuation.company_name || '',
      company_id: matchingCompany?.id || 0,
      company_measurement_date: 0, // Required by Fulfillment type but not used for Jobs
    } as ModelWithValuationData;
  }, [selectedValuation, companies]);

  const handleOpenModal = useCallback((valuation: SimplifiedAttioValuation) => {
    setSelectedValuation(valuation);
    setOpenModal(true);
    
    // Update URL with attio-id query param
    const newQueryParams = { ...queryParams, 'attio-id': valuation.record_id };
    navigate({ search: queryString.stringify(newQueryParams) }, { replace: true });
  }, [navigate, queryParams]);

  const handleCloseModal = useCallback(() => {
    setOpenModal(false);
    setSelectedValuation(undefined);
    
    // Remove attio-id from URL query params
    const newQueryParams = { ...queryParams };
    delete newQueryParams['attio-id'];
    navigate({ search: queryString.stringify(newQueryParams) }, { replace: true });
  }, [navigate, queryParams]);
  
  // Auto-open modal when attio-id is in URL and data is loaded
  useEffect(() => {
    if (attioIdFromUrl && rows.length > 0 && !openModal) {
      const matchingValuation = rows.find(row => row.record_id === attioIdFromUrl);
      if (matchingValuation) {
        setSelectedValuation(matchingValuation);
        setOpenModal(true);
      }
    }
  }, [attioIdFromUrl, rows, openModal]);

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? DESC : ASC);
    setOrderBy(property);
  };

  const handleNoteSave = async (valuationId: string, noteContent: string, currentNote?: string) => {
    const request: CreateNoteRequest = {
      content: noteContent,
      attio_valuation_id: valuationId,
      reference_type: 'at',
    };

    // Determine if we're updating an existing note or creating a new one
    const currentValuation = rows.find(r => r.record_id === valuationId);
    const hasExistingNote = Boolean(
      (currentNote && currentNote.trim() !== '' && currentNote !== '-') ||
      (currentValuation?.note?.content && currentValuation.note.content.trim() !== '')
    );

    let existingNoteId: string | undefined;
    
    if (hasExistingNote) {
      // Check if the note has a valid ID (not 0, undefined, or null)
      const noteId = currentValuation?.note?.id;
      if (noteId && noteId !== 0) {
        existingNoteId = noteId.toString();
      } else {
        // If note ID is invalid (0 or undefined), treat as new note
        existingNoteId = undefined;
      }
    }

    await handleNoteSaveGeneric(valuationId, noteContent, currentNote, request, hasExistingNote, existingNoteId);
  };

  return (
    <GenericWorkboard>
      <ValuationsTableBulkData
        isLoading={isLoading}
        rows={rows}
        onStatusUpdate={updateValuationStatus}
        onNoteUpdate={updateValuationNotes}
        onNoteSave={handleNoteSave}
        orderBy={orderBy}
        order={order}
        onRequestSort={handleRequestSort}
        isNoteLoading={isNoteLoading}
        noteError={noteError}
        onEngagementManagerUpdate={updateValuationEngagementManager}
        onRowClick={handleOpenModal}
        sectionId={sectionId}
      />
      <OperationsModal
        open={openModal}
        onClose={handleCloseModal}
        selectedModel={selectedModel}
        isLoading={isLoading}
        isFromModels={false}
      />
    </GenericWorkboard>
  );
};

export default WorkboardDataTable;
